/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAlignment,
  selectDateComparison,
  selectSummaryAlignmentToggle,
  selectSymmetricComparisonToggle,
  selectSummaryEndDate,
  selectSummaryBookingDate,
  selectSummaryStartDate,
  selectSummaryVisualToggle,
  selectPaceVisualFilterList,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
} from 'modules/dashboard/components/tab-container/summary-tab/selectors';
import {
  selectPaceHorizon,
  selectPaceVariable,
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectPmsSync,
  selectEnableHotelGroups,
  selectSummaryForecastFilter,
  selectReportType,
  selectHotelGroupDropdownList,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import { summaryActions } from 'modules/dashboard/components/tab-container/summary-tab/slice';
import { KEYS, REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import {
  getAlignmentAndDateComparisonSchema,
  getComparisonStayDateSchema,
  getDashboardPmsSyncSchema,
  getFarFieldPmsSyncSchema,
  getIsForecastSchema,
  getNormalizedViewSchema,
  getPaceHorizonSchema,
  getPaceVariableSchema,
  getPmsSyncSchema,
  getRevenueForecastSchema,
  getRoomForecastSchema,
  getTargetDateSchema,
  getMaxStayDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import writeLog from 'modules/common/utils/filter-log';
import calculateComparisonDates from '../../../../../common/utils/dateComparisonUtils';
import buildSummaryHotelFilters from '../utils/build-summary-hotel-filters';

/**
 * Custom hook to build pace visual filters for the active summary report
 * Includes hotel comparison, forecast and normalized view filters.
 */
const useSummaryPaceFilters = () => {
  const dispatch = useDispatch();
  const summaryForecastFilter = useSelector(selectSummaryForecastFilter);
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectSummaryBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const paceVariable = useSelector(selectPaceVariable);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const endDate = useSelector(selectSummaryEndDate);
  const startDate = useSelector(selectSummaryStartDate);
  const alignmentToggle = useSelector(selectSummaryAlignmentToggle);
  const visualToggle = useSelector(selectSummaryVisualToggle);
  const reportType = useSelector(selectReportType);
  const paceVisualFilterList = useSelector(selectPaceVisualFilterList);
  const hotelGroupsDropdownList = useSelector(selectHotelGroupDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);
  // Triggered when pmsSyncToggle,visualFilters,dateComparison,alignment,targetDate,hotelId,paceHorizon,paceVariable,endDate,startDate value changes
  useEffect(() => {
    if (
      reportType !== REPORT_TYPE.SUMMARY ||
      !dateComparison ||
      !alignment ||
      !targetDate ||
      !visualFilters ||
      !hotelId ||
      !paceHorizon ||
      !paceVariable ||
      (dateComparison && dateComparison === KEYS.CUSTOM && endDate === null)
    )
      return;
    // Generate filter list for summary widget pace visual
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    //
    const hotelFilters = buildSummaryHotelFilters({
      hotelId,
      hotelFocusOn,
      enableHotelGroups,
      enableSecondaryHotelGroups,
      isPrimaryHotelSetOverTime,
      invalidPrimaryHotels,
      isSecondaryHotelSetOverTime,
      invalidSecondaryHotels,
      visualFilters,
    });
    //
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonSchema(
      visualFilters,
      alignment?.id,
      dateComparison
    );
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const paceHorizonFilter = getPaceHorizonSchema(visualFilters, paceHorizon);
    const paceVariableFilter = getPaceVariableSchema(
      visualFilters,
      paceVariable,
      TAGS.SUMMARY_PACE_VARIABLE
    );
    const isForecastFilter = getIsForecastSchema(visualFilters, summaryForecastFilter);
    const roomForecastFilter = getRoomForecastSchema(visualFilters, pmsSyncToggle);
    const revenueForecastFilter = getRevenueForecastSchema(visualFilters, pmsSyncToggle);
    const farFieldPmsFilter = getFarFieldPmsSyncSchema(visualFilters, pmsSyncToggle);
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const summaryPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const { adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    //
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    //
    if (
      targetDateFilter &&
      paceHorizonFilter &&
      paceVariableFilter &&
      roomForecastFilter &&
      revenueForecastFilter &&
      alignmentAndDateComparisonFilter &&
      farFieldPmsFilter &&
      isForecastFilter &&
      normalizedFilter &&
      summaryPmsSyncSchema
    ) {
      const filters = [
        ...pmsSyncFilter,
        ...summaryPmsSyncSchema,
        targetDateFilter,
        paceHorizonFilter,
        paceVariableFilter,
        roomForecastFilter,
        revenueForecastFilter,
        ...alignmentAndDateComparisonFilter,
        ...farFieldPmsFilter,
        isForecastFilter,
        ...hotelFilters,
        normalizedFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (
        JSON.stringify(filters) !== JSON.stringify(paceVisualFilterList) &&
        reportType === REPORT_TYPE.SUMMARY
      ) {
        //
        writeLog('Pace Visuals Filter', filters);
        dispatch(summaryActions.setPaceVisualFilterList(filters));
      }
    }
  }, [
    pmsSyncToggle,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    paceHorizon,
    paceVariable,
    endDate,
    startDate,
    visualToggle,
    enableHotelGroups,
    summaryForecastFilter,
    reportType,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    hotelGroupsDropdownList,
    normalizedViewToggle,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
  ]);
};

export default useSummaryPaceFilters;
